
import { LitElement, html, css, TemplateResult, PropertyValues, svg, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageCheckbox } from '../../voyage-checkbox/src/voyage-checkbox';
import { FiSubView, UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { VoyageShoreContextModel } from '../../../src/models/voyage-shore-context-model';
import { TypeAHeadOption, VoyInputTypeAHead } from '../../voy-input-type-a-head/src/voy-input-type-a-head';
import { AlarmType } from '../../../src/services/types/fleet-alarms-service.types';
import { FilterType, OperatorId, LocationId, VoyageFilterResult, WizardStructureInterface, WizardStructureItem, VesselVoyageVersion, AisStatusId, WeatherAlarms } from './types';
import { FilterUtils } from './filter-utils';
import { ShoreContextEvent } from '../../../src/models/types/voyage-shore-context-model.types';
import { VesselsModel } from '../../../src/models/vessels-model';
import { VesselIconColors, vesselColors } from '../../../src/utils/fleet-point-icon-utils';
import { VesselCategories } from '../../../src/services/types/position-service.types';
import { VoyMapModel } from '../../../src/models/voy-map-model';
import { AuthService, AuthorizationRoles } from '../../../src/services/auth-service';
import '../../voyage-button/src/voyage-button';
import '../../voyage-checkbox/src/voyage-checkbox';
import '../../voy-input-type-a-head/src/voy-input-type-a-head';
import "@maersk-global/mds-components-core/mc-button-group";
import { GlobalSearchsVesselsMetaData } from '../../../src/models/types/vessels-model.types';
import '../../../src/models/vessels-model';

export const filters: WizardStructureInterface[] = [
    {
        id: FilterType.Service,
        name: "Service",
        opened: true,
        onlyForInternal: false
    },
    {
        id: FilterType.Type,
        name: 'Type',
        opened: false,
        onlyForInternal: false,
        items: [
            {
                id: VesselCategories.ContainerCargo,
                name: 'Container',
            },
            {
                id: VesselCategories.Tug,
                name: 'Tug',
            },
            {
                id: VesselCategories.Tanker,
                name: 'Tanker',
            },
            {
                id:  VesselCategories.RoRo,
                name: 'Ro-Ro',
            },
            {
                id:  VesselCategories.BulkCarrier,
                name: 'Bulk Carrier',
            },
            {
                id: VesselCategories.Other,
                name: 'Other',
            },
        ]
    },
    {
        id: FilterType.Operator,
        name: 'Operator',
        opened: false,
        onlyForInternal: true,
        items: [
            {
                id: 'maersk',
                name: 'Maersk',
                items: [
                    {
                        id: OperatorId.MSK,
                        name: 'Maersk Line',
                    },
                    {
                        id: OperatorId.MCC,
                        name: 'Sealand Asia',
                    },
                    {
                        id: OperatorId.SEA,
                        name: 'Sealand America',
                    },
                    {
                        id: OperatorId.SGL,
                        name: 'Sealand Europe',
                    },
                    {
                        id: OperatorId.MLL,
                        name: 'Maersk Line Limited',
                    },
                    {
                        id: OperatorId.ALI,
                        name: 'Aliança',
                    },
                    {
                        id: OperatorId.EXT,
                        name: 'Externally Managed',
                    },
                    {
                        id:OperatorId.MSKUWN,
                        name: 'Maersk Unknown',
                    }
                ]
            },
            {
                id: OperatorId.NonMSKWithBox,
                name: 'Non Maersk with Maersk cargo',
            },
            {
                id: OperatorId.NonMSK,
                name: 'Non-Maersk',
            }
        ]
    },
    {
        id: FilterType.Location,
        name: 'Location',
        opened: false,
        onlyForInternal: false,
        items: [
            {
                id: LocationId.ONVOYAGE,
                name: 'On voyage',
            },
            {
                id: LocationId.INPORT,
                name: 'In port',
            }
        ]
    },
    {
        id: FilterType.VesselClass,
        name: 'Vessel Class',
        opened: false,
    },
    {
        id: FilterType.FleetGroups,
        name: 'Fleet Groups',
        opened: false,
        onlyForInternal: true,
        items: [
            {
                id: 'sin1',
                name: 'SIN 1',
            },
            {
                id: 'sin2',
                name: 'SIN 2',
            },
            {
                id: 'cph1',
                name: 'CPH 1',
            },
            {
                id: 'cph2',
                name: 'CPH 2',
            },
            {
                id: 'mum1',
                name: 'MUM 1',
            },
            {
                id: 'mum2',
                name: 'MUM 2',
            },
            {
                id: 'nfk1',
                name: 'NFK 1',
            },
        ]
    },
    {
        id: FilterType.Alarms,
        name: 'Safety Alarms',
        opened: false,
        onlyForInternal: false,
        items: [
            {
                id: AlarmType.Stoppage,
                name: 'Stoppage',
            },
            {
                id: AlarmType.Crosstrack,
                name: 'Route deviations',
            },
            {
                id: AlarmType.Speed,
                name: 'Speed',
            },
            {
                id: AlarmType.NoPlan,
                name: 'No plan',
            }
        ]
    },
    //Commenting out route type filter for now
    // {
    //     id: FilterType.RouteType,
    //     name: 'Route Type',
    //     opened: false,
    //     onlyForInternal: false,
    //     items: [
    //         {
    //             id: RouteTypeOption.StarRoute,
    //             name: 'Star Route',
    //         },
    //         {
    //             id: RouteTypeOption.WniRoute,
    //             name: 'WNI Route',
    //         },
    //         {
    //             id: RouteTypeOption.OwnRoute,
    //             name: 'Own Route',
    //         },
    //     ]
    // },
    {
        id: FilterType.WeatherAlarms,
        name: 'Heavy Weather Alarms',
        opened: false,
        onlyForInternal: true, // As Heavy weather alarms are  not available for TC vessels - hiding these filter for the time being.
        items: [
            {
                id: WeatherAlarms.HeavyWeatherAlarm,
                name: 'Heavy weather',
            },
            {
                id: WeatherAlarms.ParametricRoll,
                name: 'Risk of parametric roll',
            },
        ]
    },

    {
        id: FilterType.AisStatus,
        name: 'AIS Status',
        opened: false,
        onlyForInternal: false,
        items: [
            {
                id: AisStatusId.UnderWayUsingEngine,
                name: 'Under way using engine'
            },
            {
                id: AisStatusId.AtAnchor,
                name: 'At anchor',
            },
            {
                id: AisStatusId.Moored,
                name: 'Moored',
            },
            {
                id: AisStatusId.NotUnderCommand,
                name: 'Not under command',
            },
            {
                id: AisStatusId.Aground,
                name: 'Aground',
            },
            {
                id: AisStatusId.ConstrainedByHerDraught,
                name: 'Constrained by her draught',
            },
            {
                id: AisStatusId.RestrictedManoeuvrability,
                name: 'Restricted manoeuvrability',
            },
            {
                id: AisStatusId.Undefined,
                name: 'Undefined',
            },
            {
                id: AisStatusId.Other,
                name: 'Other',
            },
        ]
    },
    {
        id: FilterType.Version,
        name: 'Vessel Voyage Version',
        opened: false,
        onlyForInternal: true, // As premium is not available for TC vessels - hiding these filter for the time being.
        items: [
            {
                id: VesselVoyageVersion.Premium,
                name: 'Premium',
            },
            {
                id: VesselVoyageVersion.Basic,
                name: 'Basic',
            },
            {
                id: VesselVoyageVersion.Cloud,
                name: 'Cloud',
            },
            {
                id: VesselVoyageVersion.Blank,
                name: 'Blank',
            },
        ]
    },
    
];

@customElement('voy-global-filters')
export class VoyGlobalFilters extends LitElement {
    public static isFilterApplied: boolean = false
    @property({ attribute: false }) filterResult?: string;
    @property({ attribute: false }) public shown: boolean = false;
    @property({ type: Boolean}) public _isUserInternal: boolean = AuthService.userHasRole(AuthorizationRoles.MaerskInternal);
    @property({ attribute: false }) private _show: boolean = false;
    @property({ attribute: false }) private _services: string[] = [];
    @property({ attribute: false }) private _input?: VoyInputTypeAHead | null = null;
    @property({ attribute: false }) private _currentFilters: WizardStructureInterface[] = filters;
    @property({ attribute: false }) private _commands: VoyageFilterResult[] = [];
    @property({ attribute: false }) private _optionsForTypeAHead: TypeAHeadOption[] = [];
    @property({ attribute: false }) private viewFilter: boolean|undefined = false;
    @property({ attribute: false }) private editFilter: boolean|undefined = false;
    @property({ attribute: false }) private createFilter: boolean|undefined = false;
    @property({ attribute: false }) private _timeCriticality: string = '';
    @property({ type: Boolean }) showCustomFilterButton: boolean = true;
    @property({ type: Boolean }) showImoFilter: boolean = false;
    @property({type : Array}) selectedImoList: string[] = [];
    @property({ type: Boolean }) customFilterApplied: boolean = false;
    @property({ attribute: false }) private appliedFilterName: string | null = null;
    private _storedFilterResult: string = '';
    private selectedFilter: string | null = null;

    constructor() {
        super();
        this._renderFilter = this._renderFilter.bind(this);
        this._renderServiceFilter = this._renderServiceFilter.bind(this);
        this._setLocalFilterState = this._setLocalFilterState.bind(this);
        this._renderCheckBox = this._renderCheckBox.bind(this);
        this._closeContainer = this._closeContainer.bind(this);
        this._renderHeaderForFilter = this._renderHeaderForFilter.bind(this);
        this._setOpenedPropery = this._setOpenedPropery.bind(this);
        this._renderFilterTemplate = this._renderFilterTemplate.bind(this);
        this._updateCheckedFilters = this._updateCheckedFilters.bind(this);
        this. _updateCheckedServiceFilters = this._updateCheckedServiceFilters.bind(this);
        this._renderPillComponent = this._renderPillComponent.bind(this);
        this._getServiceCodes = this._getServiceCodes.bind(this);
        this._renderServiceOption = this._renderServiceOption.bind(this);
        this._loadFilterState = this._loadFilterState.bind(this)
        this._clearFilters = this._clearFilters.bind(this);
        this._handleTimeCriticalityChange = this._handleTimeCriticalityChange.bind(this);
        this.openCustomFilter = this.openCustomFilter.bind(this);
        this._renderCustomFilters = this._renderCustomFilters.bind(this);
        this.renderImoFilter = this.renderImoFilter.bind(this);
        this.appliedFilterDetails = this.appliedFilterDetails.bind(this);
        this.numberOfActiveFilters = this.numberOfActiveFilters.bind(this);
    }

    static get styles() {
        return [
          css`
            voyage-button {
              fill: var(--neutrals-grey-04, grey);
              font-family: var(--component-heading-ff, 'serif');
              font-size: 14px;
              line-height: 20px;
              font-weight: normal;
              height: 32px;
              min-width: 90px;
            }
            voyage-button > span {
              color: var(--neutrals-grey-03, black);
              height: 14px;
            }
            h5 {
              margin: 0px;
            }
            .title-container {
              max-height: 51px;
              display: flex;
              justify-content: space-between;
              padding: 16px;
              border-bottom: 1px solid var(--neutrals-grey-05);
            }
            .title-container span {
              font-family: MaerskHeadline;
              font-size: 20px;
              line-height: 24px;
              letter-spacing: 0em;
              text-align: left;
              color: var(--waypoint-editor-coordinates-text);
            }
            .sub-wrapper {
              margin-left: 24px;
            }
            .two-col {
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
              gap: 8px;
            }
            .two-col div {
              width: 45%;
            }
            .filters-headers {
              display: flex;
              flex-direction: row;
              justify-content: start;
              align-items: center;
              flex-wrap: wrap;
              padding: 24px 0px;
              border-top: 1px solid var(--neutrals-grey-07, gray);
              cursor: pointer;
            }
            .filters-headers[opened] {
              padding: 24px 0px 16px;
            }
            h5 {
              font-family: MaerskText;
              font-size: 16px;
              line-height: 24px;
              letter-spacing: 0em;
              text-align: left;
              color: var(--waypoint-editor-coordinates-text, black);
            }
            .filter-toggle-svg {
              display: flex;
              align-items: center;
              border: none;
              background: none;
              padding: 0 8px;
            }
            .filter-toggle-svg svg {
              width: 10px;
              height: 14px;
              rotate: 90;
              fill: var(--primary-blue-900, blue);
            }
            .pill {
              background-color: #ededed;
              padding: 2px 8px 3px;
              border-radius: 36px;
              display: flex;
              flex-direction: row;
              gap: 4px;
              align-items: center;
            }
            .pill > span {
              font-family: MaerskText;
              font-size: 12px;
              text-align: center;
              color: var(--neutrals-grey-02, gray);
            }
            .pill .pill-close-btn {
              background: none;
              border: none;
              outline: none;
              cursor: pointer;
              fill: var(--neutrals-grey-02, black);
              padding: 0px;
            }
            .pill .pill-close-btn > svg {
              display: block;
              width: 8px;
              top: 1px;
              position: relative;
            }
            #service-input {
              width: 100%;
            }
            .toggable-container {
              display: none;
            }
            .toggable-container[opened] {
              display: block;
              padding-bottom: 24px;
            }
            .filters-sections:first-child {
              padding-bottom: 24px;
              border-radius: 4px 4px 0px 0px;
            }
            .services-pills-wrapper {
              display: flex;
              flex-wrap: wrap;
              flex-direction: row;
              gap: 16px;
            }
            .services-pills-wrapper[visible] {
              padding-top: 16px;
            }
            .servise-title {
              padding-bottom: 8px;
            }
            .one-col {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
            .sub-wrapper .inner-checkbox:first-child {
              padding-top: 8px;
            }
            .filters-sections:last-child .filters-headers {
              padding: 24px 0px 8px;
            }

            .filters-sections:last-child .filters-headers[opened] {
              padding: 24px 0px 16px;
              height: auto;
            }

            .filters-sections:last-child .toggable-container[opened] {
              padding-bottom: 8px;
            }
            .vt-color-icon svg {
              width: 12px;
              height: 12px;
            }
            .inner-checkbox span {
              display: flex;
              flex-direction: row;
              align-items: center;
            }
            .label {
              font-size: 14px;
              color: var(--neutrals-grey-02, black);
              font-family: MaerskText;
              cursor: pointer;
              margin-left: 8px;
            }
            .vt-color-icon-wrapper {
              display: flex;
              flex-direction: row;
              gap: 8px;
              align-items: center;
            }
            .upcoming {
              font-size: 14px;
              color: '#948b8b';
              font-family: MaerskText;
              margin-left: 8px;
              font-weight: 200px;
            }
            .gap {
              margin-bottom: 20px;
            }

            .alarm-icon svg {
              background-color: var(--voyage-alarm-red);
              height: 18px;
              width: 18px;
              border-radius: 12px;
              padding: 3px;
            }

            .dropdown {
              padding: 12px;
              width: 260px;
              border: 1px solid #cdc5c5;
              border-radius: 5px;
            }

            .saved-filter-dropdown {
              margin-bottom: 16px;
            }

            .apply-filters {
              display: flex;
              font-family: 'Maersk Text';
              font-style: normal;
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              text-align: end;
            }

            .save-icon {
              margin-top: 6px;
              margin-left: 15px;
              border: none;
              background: none;
              cursor: pointer;
            }
            .filter-headline {
              border-bottom: 1px solid var(--neutrals-grey-07, gray);
              margin-top: 17px;
              margin-bottom: 15px;
            }
            .save-filter-section {
              padding-top: 20px;
            }
            .saved-filter-headline {
              padding-bottom: 9px;
            }
            .create-filter[error] {
              border: 1px solid var(--neutrals-red-01, red);
            }

            .create-filter {
              width: 80%;
              padding: 10px;
              box-sizing: border-box;
              border: 1px solid var(--neutrals-grey-06, grey);
              border-radius: 5px;
              font-size: 14px;
            }
            .saved-filter-section {
              display: flex;
            }
            .hide-saved-filter-dropdown {
              display: none;
            }
            .up-arrow {
              padding-bottom: 10px !important;
              cursor: pointer;
            }
            .custom-filter-button {
              font-family: MaerskText;
              background-color: white;
              border: 1px solid var(--neutrals-grey-06, grey);
              padding: 8px 15px;
              font-size: 16px;
              border-radius: 5px;
              cursor: pointer;
              display: flex;
              align-items: center;
            }

            .custom-filter-button svg {
              margin-right: 8px;
            }

            :host([customFilterOpened]) {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 1000;
              display: none;
              background: rgba(0, 0, 0, 0.6);
            }
            .button-container {
              display: flex;
              width: 89%;
              background-color: #42b0d5;
              color: white;
              border: none;
              padding: 8px 15px;
              align-items: center;
              border-radius: 5px;
              justify-content: space-between;
            }
            .close-icon {
              font-family: MaerskText;
              background-color: #42b0d5;
              font-size: 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              border: none;
            }
            .close-icon svg {
              color: white;
              width: 20px;
              height: 20px;
            }
            .applied-filter-button {
              font-family: MaerskText;
              background-color: #42b0d5;
              font-size: 16px;
              cursor: pointer;
              display: flex;
              align-items: center;
              border: none;
              color: white;
            }

            .applied-filter-button svg {
              margin-right: 8px;
              color: white;
            }

            .filters-container {
              display: flex;
              flex-direction: row;
            }

            .filters-row {
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              width: 100%;
            }

            .filters-wrapper {
              flex: 1;
            }

            .imo-filter {
              flex: 1;
              padding: 10px;
              margin-left: 10px;
              border-left: 1px solid var(--neutrals-grey-06, grey);
              position: relative;
            }

            .search-input {
              width: 100%;
              padding: 12px 10px 12px 40px;
              box-sizing: border-box;
              border: 1px solid var(--neutrals-grey-06, grey);
              border-radius: 5px;
              font-size: 14px;
            }

            search-input:focus,
            search-input:active {
              outline: none;
              box-shadow: none;
            }

            search-input::placeholder {
              color: var(--neutrals-grey-05, grey);
            }

            .input-icon-container {
              position: relative;
              display: flex;
              align-items: center;
              flex-grow: 1;
              flex-direction: column;
            }

            .input-icon-container svg {
              position: absolute;
              left: 10px;
              top: 10px;
              pointer-events: none;
            }

            .dropdown-filter {
              width: 100%;
              border-radius: 5px;
              position: absolute;
              top: 100%;
              left: 0;
              background-color: white;
            }

            .sub-title {
              font-size: 12px;
              color: var(--neutrals-grey-04);
              margin: 0px;
              font-weight: 100;
            }

            .premium-icon {
              margin-top: -10px;
              position: absolute;
              margin-left: -2rem;
            }

            .icon {
              position: absolute;
              align-self: center;
              margin-left: 20rem;
              padding-bottom: 35px;
            }

            .option {
              display: flex;
              justify-content: center;
              flex-direction: column;
              list-style: none;
              cursor: pointer;
              pointer-events: all;
              font-size: 14px;
              color: var(--neutrals-grey-02, grey);
              pointer-events: all;
              border-bottom: 1px solid var(--neutrals-grey-06);
              padding-right: 16px;
              padding-left: 18px !important;
              height: 63px;
            }
            .option-item {
              display: flex;
              gap: 2rem;
              align-items: center;
            }
            .option h4 {
              margin: 0px;
              font-size: 14px;
              flex: 1 1 auto;
              color: var(--voyage-black);
              height: 20px;
              line-height: 16px;
              font-family: Maersk Text;
            }
            .option:hover {
              background-color: var(--neutrals-grey-08, grey);
            }
            .option:focus {
              background-color: var(--neutrals-grey-08, grey);
              outline: none;
            }

            .option.no-results h2,
            .option.no-results p {
              font-size: 14px;
              line-height: 20px;
              margin: 0;
            }

            .criticality {
              font-family: MaerskText;
              font-size: 14px;
            }
            .close {
              grid-column: 2;
              background: none;
              border: none;
              outline: none;
              cursor: pointer;
              padding: 12px 10px 10px;
              fill: var(--neutrals-grey-02, black);
            }

            .pagination-controls {
                position: fixed;
                bottom: 10vh;
                background-color: #fff;
                z-index: 1000;
                padding: 10px;
                border-top: 1px solid #ddd;
                width: 31vw;
                left: 50vw;
            }

            .remove-filters {
                text-decoration: underline;
                font-family: 'Maersk Text';
                        font-style: normal;
                        font-weight: 400;
                        font-size: 14px;
                        line-height: 20px;
                        color: #0072AA;
                        cursor: pointer;
                        display: flex;
                        justify-content: flex-end;
                        padding-bottom: 2px;
            }
            .filter-text{
                width: 100%;
                display: flex;
                justify-content: flex-start;
                color: grey;
                font-size: 14px;
            }
            .select-all-btn{
                background: none;
                border: none;
            }
          `,
        ]
    }

    render() {
        this.numberOfActiveFilters();
        return html`<section class="container">
            <!-- commenting out the saved filters for now -->
            ${this._renderCustomFilters()}
            <section class="filters-container" ?show=${this._show}>
            <div class="filters-row">
            <div class="filters-wrapper">
            <div class="filter-headline"></div>                   
                    ${this._currentFilters.map(filter => {
                        if(!this._isUserInternal){
                           if(!filter.onlyForInternal) return this._renderFilter(filter)
                        }
                        else  return this._renderFilter(filter)
                       }                       
                    )}
                    <div class="gap"></div>
                </div>
                ${this.showImoFilter ? html `${this.renderImoFilter()}` : html``}
                </div>
            </section>
        </section>`;
    }

    private _renderFilter(filter: WizardStructureInterface) {
        switch (filter.id) {
            case FilterType.Service: {
                return this._renderServiceFilter(filter);
            }
            case FilterType.Alarms:
            case FilterType.Type: 
            case FilterType.AisStatus:
            case FilterType.Operator:
            case FilterType.RouteType:
            case FilterType.PlansMonitoring:{
                return this._renderFilterTemplate(filter, false, true);
            }
            case FilterType.VesselClass:
            case FilterType.Location:
            case FilterType.Version:
            case FilterType.FleetGroups:
             {
                return this._renderFilterTemplate(filter, true);
            }
            case FilterType.WeatherAlarms: {                
                return this._renderFilterTemplate(filter, false, true, true);
            }
            default:
                return html`<p>something went wrong</p>`
        }
    }

    private _renderServiceOption(service: TypeAHeadOption): TemplateResult {
        return html`<div class="tp-service-option">${service.label}</div>`
    }

    private onChange(e: CustomEvent) {
        e.stopPropagation();
        const { value }: { value: string } = e.detail;
        this._selectServiceOption([value]);
        this.requestUpdate();
        const imo = UiStateModel!.getValue(UiStateModelParam.IMO);
        if (imo) {
            {
                UiStateModel!.setValue(UiStateModelParam.FISubView);
                return html`<voy-fleet-sub-view-list></voy-fleet-sub-view-list>`;
            }
        }
    }

    private _onEnter(e: KeyboardEvent) {
        e.stopPropagation();
        if (!this._input) {
            console.error("Cannot find input element when clicking ENTER");
            return;
        }

        if (this._input.listLength >= 1 && e.key === "Enter") {
            const validInputItems = this._input.validInputItems;
            this._selectServiceOption(validInputItems);
            this._input.blur();
        }
    }

    private _selectServiceOption(selectedServices: string[]) {
        // user is probably typing lowercase, so convert to uppercase
        // to make sure strings match
        const selectedUpperCaseServices = selectedServices.map((service => service.toUpperCase()))
        
        const filteredServices = selectedUpperCaseServices.filter((newService) =>
            !this._services.find((service) => service.toUpperCase() === newService)
        );
        this._services = this._services.concat(filteredServices);
        this._updateCheckedServiceFilters(true, selectedServices, FilterType.Service);
        this.requestUpdate();
    }

    private _renderServiceFilter(filter: WizardStructureInterface): TemplateResult {

        let numberOfActiveFilters = this._services.length;
        const title = numberOfActiveFilters > 0 ? `${filter.name} (${numberOfActiveFilters})` : filter.name;

        return html`
            <section class="filters-sections">
                <h5 class="servise-title">${title}</h5>
                <section class="search">
                    <voy-input-type-a-head placeholder="Search service eg. 86w, 442" .options=${this._optionsForTypeAHead} .renderItem=${this._renderServiceOption} @change=${this.onChange} ?disabled=${this.viewFilter}></voy-input-type-a-head>
                </section>
                <div class="services-pills-wrapper" ?visible=${numberOfActiveFilters > 0}>
                    ${numberOfActiveFilters > 0 ? this._services.map(pill => this._renderPillComponent(pill))
                :
                html``}
                </div>
            </section>
        `
    }

    private _renderColorIcon(color: string) {
        return html`<span class="vt-color-icon">
            ${svg`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5.5" fill="${color}" stroke="black"/>
            </svg>`}
        </span>`
    }

    private _renderVesselTypes(vesselCategory: string) {
        const color = vesselColors.find(vc => {
            return vc[0] === vesselCategory
        })?.[1]

        if(vesselCategory === VesselCategories.ContainerCargo) {
            return html`
            <div class="vt-color-icon-wrapper">
                ${this._renderColorIcon(VesselIconColors.Maersk)}
                ${this._renderColorIcon(VesselIconColors.Alarm)}
                ${this._renderColorIcon(VesselIconColors.NonMaersk)}
            </div>
        `
        }

        return html`
              ${this._renderColorIcon(color!)}
        `
    }

    private _renderCategoryIcon(category: string) {
        if(category === "premium") {
            return html`<span class="premium-icon">${SvgIcons.customPremiumVessel}</span>`
        }
    }

    private _renderAlarmIcon(alarmType: string) {
        switch(alarmType) {
            case AlarmType.Stoppage:
                return html`<span class="alarm-icon">${SvgIcons.customStoppageAlarm}</span>`
            case AlarmType.Speed:
                return html`<span class="alarm-icon">${SvgIcons.customSpeedAlarm}</span>`
            case AlarmType.Crosstrack:
                return html`<span class="alarm-icon">${SvgIcons.customOfftrackAlarm}</span>`
            case WeatherAlarms.HeavyWeatherAlarm:
                return html`<span class="">${SvgIcons.heavyWeatherAlarm}</span>`
            case WeatherAlarms.ParametricRoll:
                return html`<span class="">${SvgIcons.parametricRollAlarm}</span>`
        }
    }

    private _handleTimeCriticalityChange(e: any) {
            // deselect if the same filter is selected
            if(this._timeCriticality === e.detail.item.value) {
                this._timeCriticality = '';
                this._updateCheckedFilters(false, this._timeCriticality, "tc")
            } else {
            this._timeCriticality=e.detail.item.value 
            this._updateCheckedFilters(true, this._timeCriticality, "tc")
        }
        this.requestUpdate();
    }

    private _renderFilterTemplate(filter: WizardStructureInterface, isTwoColumnsTemplate: boolean = false, withColorIcon: boolean = false, subSection: boolean = false): TemplateResult {
        const showVesselCategoryColorIcon = filter.id === FilterType.Type;
        const alarmIcon = filter.id === FilterType.Alarms || filter.id === FilterType.WeatherAlarms;
        let items: WizardStructureItem[] | undefined = filter.items;
      
        if (!items && VesselsModel) {
          items = VesselsModel.getAllVesselsClasses()?.map((vesselClass: string) => ({
            id: vesselClass,
            name: vesselClass
          }));
        }
      
        if (!items) return html``;

        items = items.filter((item) => item.name !== "N/A" && item.name !== "Uncategorized");
        return html`
          <section class="filters-sections">
            <h5>${this._renderHeaderForFilter(filter)}</h5>
            <div class="toggable-container" data-test="two-col-template" ?opened=${filter.opened}>
              <div class=${isTwoColumnsTemplate ? "two-col" : 'one-col'}>
                ${items.map((item: WizardStructureItem) => {
                  if (item.items) {
                    return html`
                      <div>
                        ${this._renderCheckBox(item, filter.id, false, showVesselCategoryColorIcon)}
                        <div class="sub-wrapper one-col" data-test="operator-filter">
                          ${item.items.map(subItem => this._renderCheckBox(subItem, filter.id, true))}
                        </div>
                      </div>
                    `;
                  } else {
                    return this._renderCheckBox(item, filter.id, false, showVesselCategoryColorIcon, alarmIcon);
                  }
                })}
              </div>
                ${subSection ? html`<div class="criticality">
                    <p>Time criticality</p>
                    <mc-button-group width="full-width" @listchange=${this._handleTimeCriticalityChange}>
                        <mc-button-group-item ?disabled=${(this._commands.find(c => c.cmd === FilterType.WeatherAlarms)?.vals?.length === 0) ?? true} ?selected=${this._timeCriticality === "24h"} value="24h" label="24h"></mc-button-group-item>
                        <mc-button-group-item ?disabled=${(this._commands.find(c => c.cmd === FilterType.WeatherAlarms)?.vals?.length === 0) ?? true} ?selected=${this._timeCriticality === "48h"} value="48h" label="48h"></mc-button-group-item>
                        <mc-button-group-item ?disabled=${(this._commands.find(c => c.cmd === FilterType.WeatherAlarms)?.vals?.length === 0) ?? true} ?selected=${this._timeCriticality === "72h"} value="72h" label="72h"></mc-button-group-item>
                        </mc-button-group>
            </div>` : nothing}
            </div>
          </section>
        `;
      }
      
      

    private _renderHeaderForFilter({ name, id, opened }: WizardStructureInterface): TemplateResult {
        let arrWithVall = this._commands?.find(c => c.cmd === id)?.vals?.find(val => val === 'maersk');
        let numberOfActiveFilters = this._commands?.find(c => c.cmd === id)?.vals?.length;
        let num =numberOfActiveFilters && numberOfActiveFilters - (arrWithVall ? 1 : 0 );

        const title = numberOfActiveFilters && numberOfActiveFilters > 0 ? `${name} (${num})` : name;

        return html`<div class="filters-headers" ?opened=${opened}  @click=${() => this._setOpenedPropery(id)}>
             <h5 data-test=${id} class="filters-navigation">${title}</h5>
            <div class="filter-toggle-svg filters-navigation">
                ${opened ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronDown}`}
            </div>
        </div>`
    }

    private _renderPillComponent(name: string): TemplateResult {
        return html`<div class="pill">
            <span>${name}</span><button class="pill-close-btn" @click=${() => this._removePill(name)} ?disabled=${this.viewFilter}>${SvgIcons.lightTimes}</button>
        </div>`
    }

    private _renderCheckBox(item: WizardStructureItem, filterId: string, showId: boolean = false, showVesselTypeColorIcon: boolean = false, alarmIcon: boolean = false) { 
        let cmd = this._commands.find(c => c.cmd === filterId);

        
        const active = !!cmd?.vals?.find(v => v.toLowerCase() === item.id.toLowerCase());
        const { name, id } = item;
        const count = VesselsModel!.getFilterCategoryCount(id) || 0;
        let nameElement = `${name} (${count})`

        if(showId && id !== "MskUnwn") {
            nameElement = `${name} (${id}) (${count})`
        } 
        const showVcategoryIcon = id ==="premium";
        this.requestUpdate();
        return html`<div class="inner-checkbox">
            <span>
                <voyage-checkbox data-test=${id} .checked=${active} @input=${(e: Event) => this._updateCheckedFilters((<VoyageCheckbox>e.target).checked, id, filterId)} ?disabled = ${this.viewFilter}></voyage-checkbox>
                <span>${showVesselTypeColorIcon ? 
                    this._renderVesselTypes(id) : nothing}
                <span>${showVcategoryIcon? 
                    this._renderCategoryIcon(id) : nothing}</span>
                <span>${alarmIcon? this._renderAlarmIcon(id) : nothing}</span>
                    <span class="label" data-test="${id}-label">${nameElement}</span>
                </span>
            </span>
        </div>`    
    }

    private _itemForId(items: WizardStructureInterface[], itemId: string): WizardStructureInterface | null {
        for (const filter of items) {
            if (filter.id === itemId) {
                return filter;
            }
            if (!filter.items) {
                continue;
            }

            const result = this._itemForId(filter.items, itemId);
            if (result) {
                return result;
            }
        }
        return null;
    }

    private _setLocalFilterState(checked: boolean, itemId: string, filterId: string) {
        // Find the selected item from global filters in the `cmd` array with the selected filter
        const cmd = this._commands.find(c => c.cmd === filterId);

        if (cmd) {
            if (checked) {
                // If the checkbox for the selected filter is checked
                if (cmd.cmd === FilterType.TimeCriticality) {
                    // Remove all other time criticality filters
                    cmd.vals = [];
                    cmd.vals.push(itemId.toLowerCase());
                } else if (!cmd.vals?.includes(itemId.toLowerCase())) {
                    // Add the selected filter to the cmd.vals
                    cmd.vals?.push(itemId.toLowerCase());
                }
            } else {
                // If the cmd is not selected, remove it
                cmd.vals = cmd.vals?.filter(v => v.toLowerCase() !== itemId.toLowerCase());
                // If cmd.vals is empty, remove the entire cmd object
                if (cmd.vals?.length === 0) {
                    this._commands = this._commands.filter(c => c.cmd !== filterId);
                }
            }
        } else {
            // If the category was not previously selected, add it as a new `cmd` key
            if (checked) {
                const newCmd = { cmd: filterId, vals: [itemId.toLowerCase()] };
                this._commands.push(newCmd);
            }
        }
    }
    private _itemForServiceId(items: WizardStructureInterface[], itemId: string[]): WizardStructureInterface | null {
        for (const filter of items) {
            if (!filter.items) {
                continue;
            }

            const result = this._itemForServiceId(filter.items, itemId);
            if (result) {
                return result;
            }
        }
        return null;
    }
      private _updateCheckedServiceFilters(checked: boolean, itemId: string[], filterId: string) {
        
        let currentFilter: any;

        if (this.showCustomFilterButton) {
            let queryfromURL = UiStateModel?.getValue('q');
            currentFilter = FilterUtils.stringToFilter(queryfromURL || '');
            this._pushNewServiceFilters(checked, itemId, filterId);

            const item = this._itemForServiceId(this._currentFilters, itemId);
            if (item?.items) {
                item.items.forEach((item) => this._setLocalFilterState(checked, item.id, filterId));
            }
            // If the custom filter button is shown, update the filter in the URL
            this.filterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
            UiStateModel!.setValue('q', this.filterResult);
            UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.ListView);
            VoyMapModel?.setZoomLevel(this.getZoomPointFromURL() || 3);

        } else {
            this._pushNewServiceFilters(checked, itemId, filterId);

            const item = this._itemForServiceId(this._currentFilters, itemId);
            if (item?.items) {
                item.items.forEach((item) => this._setLocalFilterState(checked, item.id, filterId));
            }

            this._storedFilterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
            this.dispatchEvent(new CustomEvent('stored-filter-result-changed', {
                detail: { storedFilterResult: this._storedFilterResult },
                bubbles: true,
                composed: true
            }));
        }
        this._updateNumberOfActiveFilters();
    }
    
    private openCustomFilter() {
        this.dispatchEvent(new CustomEvent('open-custom-filter', { bubbles: true, composed: true }));
        this.requestUpdate();
    }

    private _handleCloseFilter(){
        this.dispatchEvent(new CustomEvent('close-filter', { bubbles: true, composed: true }));
    }

    private _renderCustomFilters() {     
        return html`
          <div>
            ${this.showCustomFilterButton && this.appliedFilterName
              ? html`
                  <div class="button-container">
                    <button
                      class="applied-filter-button"
                      @click="${this.openCustomFilter}"
                    >
                      ${SvgIcons.customFilterSliders}${this.appliedFilterName}
                    </button>
                    <button
                      class="close-icon"
                      @click=${this._handleCloseFilter}
                    > ${SvgIcons.lightTimesWhite}
                    </button>
                  </div>
                `
              : this.showCustomFilterButton
              ? html`
                  <button
                    class="custom-filter-button"
                    @click="${this.openCustomFilter}"
                  >
                    ${SvgIcons.customFilterSliders}Custom Filter
                  </button>
                `
              : html``}
          </div>
        `
    }

    @property({ type: Array, attribute: false }) private _shownListItems: Array<GlobalSearchsVesselsMetaData> | undefined= [];
    @property({ type: String, attribute: false }) private _searchString: string  = "";
    @property({ type: Array, attribute: false }) private _selectedVessels: Array<GlobalSearchsVesselsMetaData> = [];
    @property({ type: Number, attribute: false }) private _currentPage: number = 1;
    @property({ type: Number, attribute: false }) private _pageSize: number = 100;
    @property({ type: Number, attribute: false }) private totalPages: number = 0;

    private renderImoFilter() {
        let vesselsDataLoaded = VesselsModel?.globalSearchVesselsMetaData && VesselsModel?.globalSearchVesselsMetaData.length>0 || false;
        return html`
                <div class="imo-filter">
                ${!this.viewFilter ? html`<span class="remove-filters" @click=${() => {this._selectedVessels = []; this.selectedImoList = []}}>Clear all (${this._selectedVessels.length ?? 0})</span>` : ''}
                <div class="input-icon-container">
                            ${SvgIcons.customSearch}
                            <input 
                                type="text"
                                class="search-input"
                                placeholder="Search vessel, IMO"
                                ?disabled=${!vesselsDataLoaded || this.viewFilter}
                                @input=${(e: InputEvent) => {
                                    this._searchString = (e.target as HTMLInputElement).value;
                                    this._shownListItems = VesselsModel?.globalSearchVesselsMetaData?.filter(vessel => {
                                        return vessel?.vesselName?.toLowerCase().includes(this._searchString.toLowerCase()) || vessel?.imo?.toString()?.includes(this._searchString);
                                    });
                                    this._currentPage = 1;
                                    this.requestUpdate();
                                }}
                                />
                            ${!vesselsDataLoaded
                            ? (html`<div class="spinner"><mc-loading-indicator fit="large" label="Loading vessels"></mc-loading-indicator></div>`)
                            : nothing}
                            ${this._renderDropdown()}
                    </div>
                </div>          
        `
    }

    private _renderDropdown() {
        const searchResults = this._shownListItems || [];
        this.selectedImoList.forEach(imo => {
            const vessel = VesselsModel?.globalSearchVesselsMetaData?.find(v => v.imo?.toString() == imo);
            if (vessel) {
                
                if (this.editFilter || this.viewFilter) {
                    // remove any vessel whose IMO is not in selectedImoList
                    this._selectedVessels = this._selectedVessels.filter(v => this.selectedImoList.includes(v.imo?.toString()!));
                    
                    if (!this._selectedVessels.some(v => v.imo === vessel.imo)) {
                        this._selectedVessels.push(vessel);
                    }
                }
            }
        });
        
        const uniqueSearchResults = this._removeDuplicates(searchResults);
        const startIndex = (this._currentPage - 1) * this._pageSize;
        const endIndex = startIndex + this._pageSize;
        const paginatedResults = uniqueSearchResults.slice(startIndex, endIndex);
    
        this.totalPages = Math.ceil(uniqueSearchResults.length / this._pageSize);
        const showSelectedVessels = !this._searchString && this._selectedVessels.length > 0;
    
        return html`
            ${this._searchString && !this.viewFilter 
                ? html`<div class="filter-text"><p>Showing ${paginatedResults.length} of ${uniqueSearchResults.length} results.</p><button  class="select-all-btn" @click=${() => this._handleSelectAll(uniqueSearchResults, paginatedResults)}>
                        <span class="remove-filters">Select All</span>
                    </button></div>` 
                : nothing}
            
            <div class="dropdown-filter">
                ${this._searchString && !this.viewFilter
                    ? (uniqueSearchResults.length > 0 
                        ? this._renderList(paginatedResults) 
                        : this._renderEmptyMessage())
                    : (!this.viewFilter && !this.editFilter 
                        ? this._renderList(this._selectedVessels) 
                        : nothing)}
    
                ${(showSelectedVessels && (this.editFilter || this.viewFilter)) 
                    ? this._renderList(this._removeDuplicates(this._selectedVessels)) 
                    : nothing}
            </div>
        `;
    }

    private _renderPagination(){
        return html`
        ${this._searchString && !this.viewFilter 
            ? html `<div class="pagination-controls">
                <mc-pagination
                        totalpages=${this.totalPages}
                        currentpage=${this._currentPage}
                        @pagechange=${(e: CustomEvent) => {
                            this._currentPage = e.detail}}
                        fit='small'
                >
                </mc-pagination>
            </div>`
        : nothing}
        `
    }

    private _renderList(vessels: Array<GlobalSearchsVesselsMetaData>){
        return html`
        ${vessels.map((vessel) => {
            const isPremium = vessel.vesselVersion === VesselVoyageVersion.Premium;
            const isChecked = this._selectedVessels?.some(v => v.imo === vessel.imo);
            return html`
                <div class="option" key=${vessel.imo}>
                    <div class="option-item">
                        <voyage-checkbox ?checked=${isChecked} @input=${(e: Event) => this._handleCheckboxChange(e, vessel)} ?disabled = ${this.viewFilter}></voyage-checkbox>
                        <div class="title">                  
                            <h4><span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>${vessel.vesselName}</h4>
                            <p class="sub-title">${vessel.vesselType}</p>
                        </div>
                        <div class="icon">
                            <span>${SvgIcons.vesselListIcon}</span>
                        </div>
                    </div>
                </div>
            `;
        })}
        <div>${this._renderPagination()}</div>
    `;
    }

    private _handleCheckboxChange(event: Event, vessel: any) {
        const checkbox = event.target as HTMLInputElement;
        if (checkbox.checked) {
            // Add vessel to selected list if not already present
            if (!this._selectedVessels.some(v => v.imo === vessel.imo)) {
                this._selectedVessels = [...this._selectedVessels, vessel];
            }
        } else {
            this._selectedVessels = this._selectedVessels.filter((v) => v.imo !== vessel.imo); // Remove vessel from selected list
        }
        
        this.selectedImoList = this._selectedVessels.map(v => v.imo?.toString()!);
        const selectedImos = this._getSelectedImos();
 
        const newCmd = { cmd: FilterType.ImoGrouping, vals:  selectedImos};
        this._commands = this._commands.filter(c => c.cmd !== FilterType.ImoGrouping);
        this._commands.push(newCmd);

        let queryfromURL = UiStateModel?.getValue('q');
        const currentFilter = FilterUtils.stringToFilter(queryfromURL || '');

        this._storedFilterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
        localStorage.setItem('selectedFilter', this._storedFilterResult);
        this.dispatchEvent(new CustomEvent('stored-filter-result-changed', {
            detail: { storedFilterResult: this._storedFilterResult },
            bubbles: true,
            composed: true
        }));
    }

    private _handleSelectAll(searchResult: GlobalSearchsVesselsMetaData[], paginatedResults: GlobalSearchsVesselsMetaData[]) {
        // Add all vessels from the search results
        if(searchResult.length <= 1000){
            searchResult.forEach(vessel => {
                if (!this._selectedVessels.some(v => v.imo === vessel.imo)) {
                  this._selectedVessels.push(vessel);
                }
              });

              this.selectedImoList = searchResult.map(vessel => vessel.imo?.toString()!);
        } else {
            paginatedResults.forEach(vessel => {
                if (!this._selectedVessels.some(v => v.imo === vessel.imo)) {
                  this._selectedVessels.push(vessel);
                }
              });

              this.selectedImoList = paginatedResults.map(vessel => vessel.imo?.toString()!);
        }
        
        const selectedImos = this._getSelectedImos(); 
        const newCmd = { cmd: FilterType.ImoGrouping, vals: selectedImos };
        this._commands = this._commands.filter(c => c.cmd !== FilterType.ImoGrouping);
        this._commands.push(newCmd);
      
        let queryfromURL = UiStateModel?.getValue('q');
        const currentFilter = FilterUtils.stringToFilter(queryfromURL || '');

        this._storedFilterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
        localStorage.setItem('selectedFilter', this._storedFilterResult);
        this.dispatchEvent(new CustomEvent('stored-filter-result-changed', {
          detail: { storedFilterResult: this._storedFilterResult },
          bubbles: true,
          composed: true
        }));

        this.requestUpdate();
      }

    private _getSelectedImos(): string[] {
        return this._removeDuplicates(this._selectedVessels)
        .map(vessel => {
            // Convert imo to string, or return an empty string if imo is null or undefined
            return vessel.imo !== undefined && vessel.imo !== null ? vessel.imo.toString() : '';
        })
        .filter(imo => imo !== '');
    }

    private _renderEmptyMessage() {
        return html`
            <div class="empty-message">
                Oops, looks like there’s no match for your search.
                <p>Please check your spelling or try again...</p>
            </div>
        `;
    }

    private _removeDuplicates(vessels: Array<GlobalSearchsVesselsMetaData> | undefined) {
        const vesselMap = new Map<string, GlobalSearchsVesselsMetaData>();
    
        vessels?.forEach(vessel => {
            const imoKey = vessel.imo?.toString(); // Convert IMO to string
            if (imoKey && !vesselMap.has(imoKey)) {
                vesselMap.set(imoKey, vessel);
            }
        });
    
        return Array.from(vesselMap.values());
    }

    private appliedFilterDetails() {
        const filterString = UiStateModel!.getValue('q');
        if(filterString){
            this.appliedFilterName = localStorage.getItem('appliedFilter');
        }else {
            localStorage.setItem('appliedFilter','');
            this.appliedFilterName = '';
        }
        this.selectedFilter = localStorage.getItem('selectedFilter');
    }

    private _pushNewServiceFilters(checked: boolean, itemId: string[], filterId: string) {
        const cmd = this._commands.find(c => c.cmd === filterId);
        if (cmd) {
            if (checked) {
                itemId.forEach(id => {
                    if (!cmd.vals?.includes(id.toLowerCase())) {
                        cmd.vals?.push(id.toLowerCase());
                    }
                });
            } else {
                cmd.vals = cmd.vals?.filter(v => !itemId.includes(v.toLowerCase()));
            }
        } else {
            if (checked) {
                const newCmd = { cmd: filterId, vals: itemId.map(id => id.toLowerCase()) };
                this._commands.push(newCmd);
            }
        }
    }

    private _updateCheckedFilters(checked: boolean, itemId: string, filterId: string) {
        let currentFilter: any;
        if (this.showCustomFilterButton) {
            let queryfromURL = UiStateModel?.getValue('q');
            currentFilter = FilterUtils.stringToFilter(queryfromURL || '');
            UiStateModel!.setLeftPanelVisibility(true,"hide-anchor");// change the filter result panel view to open
            UiStateModel!.setShowAnchor(true);
            this._setLocalFilterState(checked, itemId, filterId);
            const item = this._itemForId(this._currentFilters, itemId);
            if (item?.items) {
                item.items.forEach((item) => this._setLocalFilterState(checked, item.id, filterId));
            }

            // If the custom filter button is shown, update the filter in the URL
            this.filterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
            UiStateModel!.setValue('q', this.filterResult);
            UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.ListView);
            VoyMapModel?.setZoomLevel(this.getZoomPointFromURL() || 3);

        } else{
            this._setLocalFilterState(checked, itemId, filterId);
            const item = this._itemForId(this._currentFilters, itemId);
            if (item?.items) {
                item.items.forEach((item) => this._setLocalFilterState(checked, item.id, filterId));
            }
            currentFilter = null;
            this._storedFilterResult = FilterUtils.filterToString({ commands: this._commands, text: currentFilter?.text });
            this.dispatchEvent(new CustomEvent('stored-filter-result-changed', {
                detail: { storedFilterResult: this._storedFilterResult },
                bubbles: true,
                composed: true
            }));
        } 
        
        this._updateNumberOfActiveFilters();
    }

    private _updateNumberOfActiveFilters() {
        const numberOfActiveFilters = this._commands.reduce((prev, curr) => {
            if ( curr!.vals!.includes('maersk')) {
                return prev + curr!.vals!.length - 1 ;
            } else if (curr!.cmd === 'tml') {
                return prev;
            }
            return prev + curr!.vals!.length
        }, 0);
        this.dispatchEvent(new CustomEvent("filters-changed", {
            detail: {
                numberOfActiveFilters,
            }
        }))
    }

    private _setOpenedPropery(id: string) {
        this._currentFilters = this._currentFilters.map(el => {
            if (el.id === id) {
                el.opened = !el.opened;
            }
            return el;
        })
    }
    private _closeContainer() {
        this._show = false;
    }

    private _removePill(selectedService: string) {
        this._services = this._services.filter(item => item !== selectedService);
        this._updateCheckedFilters(false, selectedService, FilterType.Service);
    }

    private _getServiceCodes() {
        this._optionsForTypeAHead = VoyageShoreContextModel!.services.map(el => {
            return { value: el, label: el }
        })
    }

    private _loadFilterState() {
        if(this.viewFilter && this.selectedFilter){
            const filterString = this.selectedFilter;
            const filter = FilterUtils.stringToFilter(filterString);
            if (!filter?.commands.length) {
                return;
            }
            this._commands = filter?.commands;
            for (const command of filter?.commands) {
                if (command.cmd == FilterType.Service && command.vals?.length) {
                    this._services = command.vals;
                }
            }  
            this.selectedImoList = filter?.commands.find(c => c.cmd === FilterType.ImoGrouping)?.vals || [];

            return;
        }
        const filterString = UiStateModel!.getValue('q');
        
        if (!filterString?.length) {
            return;
        }
        const filter = FilterUtils.stringToFilter(filterString);
        
        if (!filter?.commands.length) {
            return;
        }
        // set services (pills)
        for (const command of filter?.commands) {
            if (command.cmd == FilterType.Service && command.vals?.length) {
                this._services = command.vals;
            }
        }
        // set commands (count of enabled filters)
        this._commands = filter?.commands;
    }

    public _clearFilters(){
        this._commands = [];
        this.filterResult = "";
        this._services = []; 
        this._loadFilterState();
        this._timeCriticality = '';
        this._selectedVessels = [];
        this._storedFilterResult = '';
        localStorage.setItem('appliedFilter', '');
    }

    private getZoomPointFromURL(){
        const searchParams = new URLSearchParams(window.location.search);
        const zoomLevel = (searchParams?.get('at'))?.split(",")
        if(zoomLevel && zoomLevel[2]){
           return parseFloat(zoomLevel[2].replace("z",""))
        }
    }

    private numberOfActiveFilters() {
        //As global filters component is never disconnected from DOM, so the filters state need to be updated before filters applied check
        if (!this._commands || this._commands.length === 0) {
            this._loadFilterState();
        }

        const numberOfActiveFilters = this._commands.reduce((prev, curr) => {
            if (curr!.vals!.includes('maersk')) {
                return prev + curr!.vals!.length - 1 ;
            } else if (curr!.cmd === 'tml') {
                return prev;
            }
            return prev + curr!.vals!.length;
        }, 0);

        VoyGlobalFilters.isFilterApplied = numberOfActiveFilters > 0;
        this.appliedFilterDetails();
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("shown")) {
            // global-filters component is never disconnected from DOM,
            // so removing listeners here to avoid listening when not shown
            if (this.shown) {
                this.addEventListener("keydown", this._onEnter)
                this._loadFilterState();
            } else {
                this.removeEventListener("keydown", this._onEnter)
            }
        }
    }

    firstUpdated() {
        this._input = this.shadowRoot?.querySelector(".input-type-a-head");
        this._loadFilterState()
        this._getServiceCodes();
        this._updateNumberOfActiveFilters();
    }

    private _isVesselLoaded() {
        this.requestUpdate();
    }

    connectedCallback() {
        super.connectedCallback();
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.ServiceCodeChange, this._getServiceCodes);
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.ClearFilters, this._clearFilters);
        VesselsModel!.addEventListener("vessels-loaded", () => this._isVesselLoaded())
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.ServiceCodeChange, this._getServiceCodes);
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.ClearFilters, this._clearFilters);
        VesselsModel!.removeEventListener("vessels-loaded", () => this._isVesselLoaded())
    }

}
